import { useEffect, useMemo } from 'react';

import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import { useNavigate } from 'react-router-dom';

import ListTransaction from '#client/components/ListTransaction';
import { FINISH_CHECK_DEPOSIT, REFRESH_AFTER_INSTANT_DEPOSIT } from '#client/constants/eventName';
import { RENDER_TRANSACTION_TYPE } from '#client/constants/transaction';
import useBankingPath from '#client/hooks/useBankingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEventListeners from '#client/hooks/useEventListeners';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import ListPendingSection from '#client/pages/TransactionHistory/components/ListPendingSection';
import { GET_LIST_TRANSACTION, GET_PENDING_SECTION } from '#client/queries';
import { formatTransaction } from '#client/utils/transaction';

import SectionTitle from '../SectionTitle';
import NoTransaction from './components/NoTransaction';
import TransactionSkeleton from './components/SkeletonTransaction';

import { NoTransactionWrapper } from './styles';
import SectionLink from '../ConnectedAccounts/components/SectionLink';

const TRANSACTION_ITEM_LIMIT = 7;

export default function Transactions() {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const navigate = useNavigate();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId, isValidMfa, isEnabled, isEnrolled, isFetchingMfaStatus } =
    useUnitAccountId();
  const { createCommonErrorToast } = useToastContext();
  const { isOpeningModal } = useCheckOpenModal();
  const bankingPath = useBankingPath();

  const isSkipQuery =
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    !unitAccountId ||
    isOpeningModal;

  const {
    error: errorPendingSection,
    loading: loadingPendingSection,
    data: originalPendingSectionData,
    refetch: refetchPendingSection
  } = useQuery(GET_PENDING_SECTION, {
    variables: {
      params: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        pagination: {
          limit: TRANSACTION_ITEM_LIMIT,
          offset: 0
        }
      }
    },
    noThrow: true,
    skipCache: true,
    skip: isSkipQuery
  });

  useError(errorPendingSection);
  const pendingSectionData = useMemoObject(originalPendingSectionData);

  const { items: pendingSectionList } = pendingSectionData?.pendingSection || {};

  const pendingSection = useMemo(() => {
    if (!pendingSectionList) {
      return [];
    }

    const transform = (obj) => {
      if (!obj) {
        return null;
      }
      const { fullAccountNumber, accountNumber, ...rest } = obj;
      return {
        ...rest,
        accountNumberLast4: accountNumber,
        accountNumber: fullAccountNumber
      };
    };

    return pendingSectionList.map((item) => ({
      ...item,
      from: transform(item.from),
      to: transform(item.to)
    }));
  }, [pendingSectionList]);

  const {
    loading: loadingTransaction,
    error: transactionError,
    errors: transactionErrors,
    refetch: refetchTransactions,
    data: originalData
  } = useQuery(GET_LIST_TRANSACTION, {
    variables: {
      params: {
        filter: {
          businessEntityUuid
        },
        pagination: {
          limit: TRANSACTION_ITEM_LIMIT,
          offset: 0
        }
      }
    },
    noThrow: true,
    skipCache: true,
    skip: isSkipQuery
  });

  useLogError(transactionErrors, transactionError);
  const data = useMemoObject(originalData);

  const monthTransactions = useMemo(() => {
    if (pendingSection.length === TRANSACTION_ITEM_LIMIT) {
      return formatTransaction(data?.transactions?.data ?? []);
    }
    return formatTransaction(
      data?.transactions?.data.slice(0, TRANSACTION_ITEM_LIMIT - pendingSection.length) ?? []
    );
  }, [data, pendingSection]);

  const isHideSkeleton =
    (data && pendingSectionData && !loadingTransaction && !loadingPendingSection) ||
    transactionError;
  const [showSkeleton, setShowSkeleton] = useSkeleton(isHideSkeleton);

  const refetchPayment = () => {
    refetchPendingSection();
    setShowSkeleton();
  };

  const renderEmptyType = useMemo(() => {
    const finishedLoading = !loadingTransaction && !loadingPendingSection;

    const isEmptyData = monthTransactions.length === 0 && pendingSection.length === 0;
    const isEmptyPendingData = monthTransactions.length !== 0 && pendingSection.length === 0;
    const isEmptyTransactionData = monthTransactions.length === 0 && pendingSection.length !== 0;
    const isPendingData = pendingSection.length === TRANSACTION_ITEM_LIMIT;

    const isEmpty = finishedLoading && isEmptyData;

    const isEmptyPending = finishedLoading && isEmptyPendingData;
    const isPending = finishedLoading && isPendingData;
    const isEmptyTransaction = finishedLoading && isEmptyTransactionData;

    if (isEmpty) {
      return RENDER_TRANSACTION_TYPE.EMPTY;
    }

    if (isEmptyPending) {
      return RENDER_TRANSACTION_TYPE.EMPTY_PENDING;
    }

    if (isEmptyTransaction) {
      return RENDER_TRANSACTION_TYPE.EMPTY_TRANSACTION;
    }

    if (isPending) {
      return RENDER_TRANSACTION_TYPE.PENDING;
    }

    return null;
  }, [pendingSection, monthTransactions, loadingTransaction, loadingPendingSection]);

  useEventListeners([
    { eventName: FINISH_CHECK_DEPOSIT, eventHandler: refetchPendingSection },
    { eventName: REFRESH_AFTER_INSTANT_DEPOSIT, eventHandler: refetchTransactions }
  ]);

  const transactionHistoryPath = `${bankingPath}/transactions/history`;

  const handleSeeAllPressed = useLinkPressedCallback({
    elementText: 'See all',
    appPageName: 'active_banking_dashboard',
    elementName: 'transaction_see_all'
  });

  const seeAllCta = (
    <Box
      mt={isDesktop ? '8px' : '16px'}
      textAlign="center"
    >
      <LoadingButton
        aria-label="see all transactions"
        onClick={() => {
          handleSeeAllPressed();
          navigate(transactionHistoryPath);
        }}
        variant="outlined"
      >
        See all
      </LoadingButton>
    </Box>
  );

  const listTransaction = useMemo(() => {
    switch (renderEmptyType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <NoTransaction />;
      case RENDER_TRANSACTION_TYPE.EMPTY_PENDING:
        return (
          <NoTransactionWrapper>
            <ListTransaction
              hasMore={false}
              isLoading={loadingTransaction}
              monthTransactions={monthTransactions}
              /**
               * shouldFormatMoneyWithColor is defined to apply a new color to the amount
               * and should be removed when applying a new banking dashboard
               */
              shouldFormatMoneyWithColor
            />
            {seeAllCta}
          </NoTransactionWrapper>
        );
      case RENDER_TRANSACTION_TYPE.EMPTY_TRANSACTION:
      case RENDER_TRANSACTION_TYPE.PENDING:
        return (
          <NoTransactionWrapper>
            <ListPendingSection
              data={pendingSection}
              isPending
              refetchPayment={refetchPayment}
              /**
               * shouldFormatMoneyWithColor is defined to apply a new color to the amount
               * and should be removed when applying a new banking dashboard
               */
              shouldFormatMoneyWithColor
              title="PENDING"
            />
            {seeAllCta}
          </NoTransactionWrapper>
        );
      default:
        return (
          <NoTransactionWrapper>
            <ListPendingSection
              data={pendingSection}
              isPending
              refetchPayment={refetchPayment}
              /**
               * shouldFormatMoneyWithColor is defined to apply a new color to the amount
               * and should be removed when applying a new banking dashboard
               */
              shouldFormatMoneyWithColor
              title="PENDING"
            />
            <ListTransaction
              hasMore={false}
              isLoading={loadingTransaction}
              monthTransactions={monthTransactions}
              /**
               * shouldFormatMoneyWithColor is defined to apply a new color to the amount
               * and should be removed when applying a new banking dashboard
               */
              shouldFormatMoneyWithColor
            />
            {seeAllCta}
          </NoTransactionWrapper>
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTransaction, monthTransactions, pendingSection, renderEmptyType]);

  useEffect(() => {
    const errors = transactionError || errorPendingSection;
    if (errors) {
      createCommonErrorToast({ error: errors });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionError, errorPendingSection]);

  return (
    <Stack
      gap={{ xs: '8px', md: '12px' }}
      marginBottom={{ xs: '8px', md: '12px' }}
      marginTop={{ xs: '16px', md: '32px' }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
      >
        <SectionTitle>Transactions</SectionTitle>
        <SectionLink
          text="See all"
          trackingProperties={{
            elementName: 'transaction_see_all'
          }}
          url={transactionHistoryPath}
        />
      </Stack>
      {showSkeleton ? (
        <NoTransactionWrapper>
          <TransactionSkeleton />
        </NoTransactionWrapper>
      ) : (
        listTransaction
      )}
    </Stack>
  );
}
